// src/contextPage/CartContext.js

import { createContext, useContext, useEffect, useMemo, useState } from "react";
import * as cartService from "../services/api";

const CartContext = createContext();

const SNAPSHOTS_KEY = "cart_product_snapshots";

const getSnapshotKey = (productId, variantId) =>
  `${productId}_${variantId ?? "0"}`;

export const CartProvider = ({ children }) => {
  const [rawCart, setRawCart] = useState([]);
  const [productSnapshots, setProductSnapshots] = useState(() => {
    try {
      const s = localStorage.getItem(SNAPSHOTS_KEY);
      return s ? JSON.parse(s) : {};
    } catch {
      return {};
    }
  });
  const [loadingCart, setLoadingCart] = useState(false);

  // Persist snapshots to localStorage when they change
  useEffect(() => {
    try {
      localStorage.setItem(SNAPSHOTS_KEY, JSON.stringify(productSnapshots));
    } catch (e) {
      console.warn("Could not persist cart snapshots", e);
    }
  }, [productSnapshots]);

  // Load cart when token exists
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      loadCart();
    }
  }, []);

  // ================= LOAD CART =================
  const loadCart = async () => {
    setLoadingCart(true);
    try {
      const res = await cartService.fetchCart();
      setRawCart(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      if (err?.response?.status === 401) {
        setRawCart([]);
      } else {
        console.error("Failed to load cart:", err);
      }
    } finally {
      setLoadingCart(false);
    }
  };

  // ================= ADD TO CART =================
  const addToCart = async (productId, quantity = 1, variantId = null, productSnapshot = null) => {
    try {
      const pidStr = String(productId);
      const variantKey = String(variantId ?? "null");
      const already = (rawCart || []).some((item) => {
        const itemPid = String(item.product_id ?? item.productId ?? item.id);
        const itemVid = String(item.variant_id ?? "null");
        return itemPid === pidStr && itemVid === variantKey;
      });

      if (already) {
        return { ok: false, alreadyInCart: true };
      }

      await cartService.addToCart(productId, quantity, variantId);
      if (productSnapshot && productId != null) {
        const key = getSnapshotKey(String(productId), variantId);
        setProductSnapshots((prev) => ({
          ...prev,
          [key]: {
            productName: productSnapshot.productName ?? productSnapshot.name,
            unitPrice: Number(
              productSnapshot.unitPrice ??
                productSnapshot.price ??
                productSnapshot.cost ??
                productSnapshot.displayPrice
            ) || 0,
            images: Array.isArray(productSnapshot.images)
              ? productSnapshot.images
              : typeof productSnapshot.images === "string"
              ? (() => {
                  try {
                    return JSON.parse(productSnapshot.images || "[]");
                  } catch {
                    return [];
                  }
                })()
              : [],
          },
        }));
      }
      await loadCart();
      return { ok: true };
    } catch (err) {
      if (err?.response?.status === 401) {
        return { ok: false, unauthorized: true };
      }
      console.error("Add to cart failed:", err);
      return { ok: false };
    }
  };

  // ================= UPDATE QUANTITY =================
  const updateQuantity = async (productId, quantity, variantId = null) => {
    const qty = Number(quantity);

    if (!Number.isFinite(qty) || qty < 1) {
      return { ok: false };
    }

    try {
      await cartService.setCartQuantity(productId, qty, variantId);
      await loadCart();
      return { ok: true };
    } catch (err) {
      if (err?.response?.status === 401) {
        return { ok: false, unauthorized: true };
      }
      console.error("Update quantity failed:", err);
      return { ok: false };
    }
  };

  // ================= REMOVE ITEM =================
  const removeFromCart = async (productId, variantId = null) => {
    try {
      await cartService.removeFromCart(productId, variantId);
      const key = getSnapshotKey(String(productId), variantId);
      setProductSnapshots((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      await loadCart();
      return { ok: true };
    } catch (err) {
      if (err?.response?.status === 401) {
        return { ok: false, unauthorized: true };
      }
      console.error("Remove from cart failed:", err);
      return { ok: false };
    }
  };

  // ================= MERGED CART (backend items + frontend snapshots for display) =================
  const cart = useMemo(() => {
    return (rawCart || []).map((item) => {
      const pid = String(item.product_id ?? item.productId ?? item.id);
      const vid = item.variant_id ?? null;
      const key = getSnapshotKey(pid, vid);
      const snap = productSnapshots[key];
      if (!snap) return item;
      return {
        ...item,
        productName: snap.productName ?? item.productName,
        unitPrice: snap.unitPrice ?? item.unitPrice ?? item.price ?? item.cost,
        images: Array.isArray(snap.images) && snap.images.length > 0 ? snap.images : item.images,
      };
    });
  }, [rawCart, productSnapshots]);

  // ================= DERIVED VALUES =================

  // Resolve unit price from cart item (backend may use unitPrice, price, cost, displayPrice, or nested product/variant)
  const getCartItemUnitPrice = (item) => {
    const raw =
      item.unitPrice ??
      item.price ??
      item.cost ??
      item.displayPrice ??
      item.product?.price ??
      item.product?.cost ??
      item.product?.displayPrice ??
      item.variant?.price;
    return Number(raw) || 0;
  };

  // Cart badge: number of distinct items/lines (same product added multiple times = 1 count)
  const cartCount = useMemo(() => cart.length, [cart]);

  // Subtotal calculation (use same price resolution so totals match cart display)
  const cartSubtotal = useMemo(() => {
    return cart.reduce((sum, item) => {
      const unitPrice = getCartItemUnitPrice(item);
      const qty = Number(item.quantity ?? 0);
      return sum + unitPrice * qty;
    }, 0);
  }, [cart]);

  return (
    <CartContext.Provider
      value={{
        cart,
        loadingCart,
        loadCart,
        addToCart,
        updateQuantity,
        removeFromCart,
        cartCount,
        cartSubtotal,
        getCartItemUnitPrice,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);